import { ValueUnit } from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import { QuoteChanges } from 'types/suggestions/QuoteSuggestions';
import { asArrayOrObjectValues } from 'utils/asArrayOrObjectValues';
import { normalizeLtlFreightClassSuggestion } from 'utils/densityToFreightClass';

import {
  LTLItem,
  LTLSelectedAccessorial,
  PACKAGE_TYPE_TO_CODE,
} from '../types';

type SuggestedCommodity = NonNullable<QuoteChanges['commodities']>[number];
type SuggestedAccessorial = NonNullable<QuoteChanges['accessorials']>[number];

export type MapSuggestedCommoditiesOptions = {
  shouldNormalizeFreightClass: boolean;
  isThreeGActive: boolean;
};

export function toPositiveNumber(value: unknown): Maybe<number> {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  return null;
}

// Suggestion arrays can arrive as objects with numeric keys after state serialization.
export function getSuggestedCommodities(
  suggestedFields: Undef<QuoteChanges>
): SuggestedCommodity[] {
  return asArrayOrObjectValues(
    suggestedFields?.commodities as Undef<
      SuggestedCommodity[] | Record<string, SuggestedCommodity>
    >
  );
}

export function mapSuggestedCommoditiesToLTLItems(
  commodities: SuggestedCommodity[],
  {
    shouldNormalizeFreightClass,
    isThreeGActive,
  }: MapSuggestedCommoditiesOptions
): LTLItem[] {
  return commodities.map((commodity) => {
    const quantity = toPositiveNumber(commodity.quantity);
    const handlingUnits = toPositiveNumber(commodity.handlingQuantity);
    const pieces = extractValueUnitAsNumber(
      commodity.totalPieces as Undef<ValueUnit | number | string>
    );
    const freightClass = shouldNormalizeFreightClass
      ? normalizeLtlFreightClassSuggestion(commodity.freightClass)
      : commodity.freightClass;

    return {
      itemId: crypto.randomUUID(),
      description: commodity.description?.trim() ?? '',
      commodityExternalTmsId: commodity.externalTmsId,
      length: commodity.length,
      width: commodity.width,
      height: commodity.height,
      weight: commodity.weightTotal,
      quantity: quantity ?? undefined,
      handlingUnits: handlingUnits ?? undefined,
      pieces: pieces ?? undefined,
      freightClass,
      ...(isThreeGActive ? { freightClassLocked: freightClass !== '' } : {}),
      nmfcCode: commodity.nmfc,
      canSideLoad: false,
      splitDepth: 0,
      packageType: PACKAGE_TYPE_TO_CODE['Pallet'],
    };
  });
}

export function mapSuggestedAccessorialsToLTLSelected(
  suggestedFields: Undef<QuoteChanges>
): LTLSelectedAccessorial[] {
  const suggestedAccessorials = asArrayOrObjectValues(
    suggestedFields?.accessorials as Undef<
      SuggestedAccessorial[] | Record<string, SuggestedAccessorial>
    >
  );

  return suggestedAccessorials.map((acc) => ({
    code: acc.code,
    name: acc.name,
    externalId: acc.externalId,
  }));
}

function extractValueUnitAsNumber(
  value: Undef<ValueUnit | number | string>
): Maybe<number> {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'object' && 'val' in value) {
    return toPositiveNumber(value.val);
  }

  return toPositiveNumber(value);
}
